import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { X, TrendingDown, TrendingUp, Calendar, FileText } from 'lucide-react';
import { getDateKeyColombia, formatDateHeaderColombia, formatTimeColombia } from '../utils/dateFormat';

interface CreditTransaction {
  id: string;
  amount: number;
  description: string;
  points_count: number;
  map_id: string | null;
  created_at: string;
  is_read: boolean;
  charge_type?: 'promocional' | 'venta' | null;
  maps?: {
    name: string | null;
  } | null;
}

interface CreditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export const CreditHistoryModal: React.FC<CreditHistoryModalProps> = ({ isOpen, onClose, userId }) => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchData();
    }
  }, [userId, isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase()
        .from('credit_transactions')
        .select(`*, maps ( name )`)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (fetchError) throw fetchError;

      const list = (data || []) as CreditTransaction[];
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
        console.debug('[CreditHistoryModal] Transacciones para', userId, ':', list.length, list);
      }
      setTransactions(list);
    } catch (err) {
      console.error('Error al obtener movimientos:', err);
      setError('Error al cargar los movimientos financieros');
    } finally {
      setLoading(false);
    }
  };

  // Formatear créditos (igual que el saldo en CreditDisplay)
  const formatearCreditos = (valor: number) => {
    return new Intl.NumberFormat('es-CO').format(valor);
  };

  // Calcular estadísticas
  const totalConsumido = transactions
    .filter(t => t.amount < 0)
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  
  const totalAgregado = transactions
    .filter(t => t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);

  // Agrupar por fecha en zona horaria Colombia (más reciente primero)
  const transactionsByDate = transactions.reduce<Record<string, CreditTransaction[]>>((acc, t) => {
    const dateKey = getDateKeyColombia(t.created_at);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(t);
    return acc;
  }, {});

  const sortedDates = Object.keys(transactionsByDate).sort((a, b) => b.localeCompare(a));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1F2937] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Movimientos Financieros</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Estadísticas */}
        <div className="p-6 border-b border-gray-700 grid grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-1">
              <TrendingDown className="w-5 h-5 text-red-400" />
              <span className="text-sm text-gray-400">Créditos gastados</span>
            </div>
            <p className="text-lg font-bold text-red-400">{formatearCreditos(totalConsumido)}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-1">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-400">Créditos adicionales</span>
            </div>
            <p className="text-lg font-bold text-green-400">{formatearCreditos(totalAgregado)}</p>
          </div>
        </div>

        {/* Lista de transacciones */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-red-400 text-center py-12">{error}</div>
          ) : transactions.length === 0 ? (
            <div className="text-gray-400 text-center py-12">
              No hay movimientos financieros registrados
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map((dateKey) => (
                <div key={dateKey}>
                  <div className="sticky top-0 z-10 py-2 mb-3 bg-[#1F2937] border-b border-gray-600">
                    <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                      {formatDateHeaderColombia(dateKey)}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {transactionsByDate[dateKey].map((transaction) => {
                      const isRecarga = transaction.amount > 0;
                      const getChargeType = (): 'promocional' | 'venta' | null => {
                        if (transaction.charge_type === 'promocional' || transaction.charge_type === 'venta') {
                          return transaction.charge_type;
                        }
                        const desc = (transaction.description || '').toLowerCase();
                        if (desc.includes('venta')) return 'venta';
                        if (desc.includes('promocional')) return 'promocional';
                        return null;
                      };
                      const chargeType = isRecarga ? getChargeType() : null;

                      return (
                        <div
                          key={transaction.id}
                          className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 hover:border-gray-600 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <div className={`mt-0.5 flex-shrink-0 ${
                                isRecarga ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {isRecarga ? (
                                  <TrendingUp size={20} />
                                ) : (
                                  <TrendingDown size={20} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    {isRecarga ? 'Créditos adicionales' : 'Créditos gastados'}
                                  </span>
                                  {chargeType && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      chargeType === 'venta' ? 'bg-green-900/50 text-green-300' : 'bg-purple-900/50 text-purple-300'
                                    }`}>
                                      {chargeType === 'venta' ? 'Por venta' : 'Promocional'}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-200 font-medium mt-1">
                                  {transaction.maps?.name || transaction.description}
                                </p>
                                {transaction.points_count > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {transaction.points_count} puntos
                                  </p>
                                )}
                                <div className="flex items-center space-x-2 mt-1">
                                  <Calendar size={12} className="text-gray-500" />
                                  <span className="text-xs text-gray-500">
                                    {formatTimeColombia(transaction.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <span
                                className={`text-base font-bold ${
                                  isRecarga ? 'text-green-400' : 'text-red-400'
                                }`}
                              >
                                {isRecarga ? '+' : '-'}
                                {formatearCreditos(Math.abs(transaction.amount))}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditHistoryModal;
